import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, Navigate } from "react-router-dom";
import { Loader2, Building2, Activity, Lock, AlertTriangle } from "lucide-react";

/**
 * Dev-only calendar-library comparison playground
 * (plans/calendar-library-comparison.md). Lazily imported so its dependencies
 * (dnd-kit, vis-timeline, react-calendar-timeline) never enter the production
 * bundle — the postbuild bundle-budget gate would fail otherwise.
 */
const CalendarPlayground = import.meta.env.DEV
  ? lazy(() => import("../dev/CalendarPlayground"))
  : null;
import OnboardingWizard from "../components/OnboardingWizard";
import SupabaseAuthForm from "../components/SupabaseAuthForm";
import ResetPassword from "../components/ResetPassword";
import GuestPortalLayout from "../features/guest-portal";
import PublicOfferConfirmation from "../components/PublicOfferConfirmation";
import PublicBusinessClubSignup from "../components/PublicBusinessClubSignup";
import PublicBusinessClubConfirm from "../components/PublicBusinessClubConfirm";
import AuthedApp from "../AuthedApp";
import { logGlobalAction } from "../constants";
import type { AppUser } from "../hooks/useHRData";

/** Shape of a tenants-table data JSONB blob relevant to routing. */
interface TenantConfig {
  onboardingCompleted?: boolean;
  [key: string]: unknown;
}

interface AppRouterProps {
  isLoaded: boolean;
  userId: string | null;
  user: AppUser | null;
  userRole: string | null;
  isApaleoAuthed: boolean;
  setIsApaleoAuthed: (v: boolean) => void;
  loadingToken: boolean;
  subscriptionStatus: string;
  tenantConfig: TenantConfig | null;
  setTenantConfig: (config: TenantConfig) => void;
  globalAdmins: string[];
  signOut: () => Promise<void>;
  getToken: () => Promise<string | null>;
  isOffline: boolean;
}

/**
 * Route-protection layer and top-level render decision tree for AdaptHub.
 *
 * Receives the fully-resolved auth/session state from `useAppAuth` (plus the
 * `isOffline` flag from `useAppRealtime`) and decides which surface to render:
 * loading gate, public offer confirmation, password reset, guest portal,
 * sign-in screen, onboarding wizard/lock, tenant-resolution wait, subscription
 * suspension, or the authenticated dashboard (`AuthedApp`).
 *
 * @param {Object} props - Auth + realtime state from the App shell.
 * @returns {JSX.Element} The appropriate surface for the current route/session.
 */
export function AppRouter({
  isLoaded,
  userId,
  user,
  userRole,
  isApaleoAuthed,
  setIsApaleoAuthed,
  loadingToken,
  subscriptionStatus,
  tenantConfig,
  setTenantConfig,
  globalAdmins,
  signOut,
  getToken,
  isOffline,
}: AppRouterProps) {
  const { t } = useTranslation();
  const location = useLocation();

  const hasSession = !!userId;

  if (!isLoaded || loadingToken || (hasSession && !user)) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50"
      >
        <div className="text-center">
          <Loader2
            className="animate-spin mb-6 mx-auto text-[#859F4A]"
            size={48}
          />
          <p
            className="text-slate-900 font-medium"
          >
            Lade Adapt Hub...
          </p>
        </div>
      </div>
    );
  }

  // Dev-only calendar playground — no auth, no tenant, no Supabase.
  // Guarded by import.meta.env.DEV so the route does not exist in production.
  if (CalendarPlayground && location.pathname === "/dev/calendar-playground") {
    return (
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <Loader2 className="animate-spin text-[#859F4A]" size={40} />
          </div>
        }
      >
        <CalendarPlayground />
      </Suspense>
    );
  }

  // Public offer confirmation page (no auth required, must be before portal/auth checks)
  if (location.pathname.startsWith("/offer/")) {
    const parts = location.pathname.split("/");
    return <PublicOfferConfirmation tenantId={parts[2]} offerId={parts[3]} />;
  }

  // Public BusinessClub sign-up page (no auth required, same pattern as /offer/)
  if (location.pathname.startsWith("/businessclub/")) {
    const parts = location.pathname.split("/");
    // SECURITY (audit M-8): /businessclub/confirm/<tenantId> is the double
    // opt-in landing page — the confirmation link from the opt-in email lands
    // here. Distinguished from the signup path (parts[2] = tenantId).
    if (parts[2] === "confirm") {
      return <PublicBusinessClubConfirm tenantId={parts[3]} />;
    }
    return <PublicBusinessClubSignup tenantId={parts[2]} />;
  }

  // Password reset page (no auth required; session is recovery-only)
  if (location.pathname === "/portal/reset-password") {
    return <ResetPassword />;
  }

  // Route protection and Guest Portal injection
  if (location.pathname === "/portal" || location.pathname.startsWith("/portal/")) {
    // GuestPortalLayout (outside this slice) accepts `[key: string]: any`; cast at boundary.
    return <GuestPortalLayout user={user as { tenantId?: string; [key: string]: unknown } | null} />;
  }

  if (user && user.role === 'guest') {
    return <Navigate to={`/portal/${user.tenantId}/dashboard`} replace />;
  }

  // `user` is guaranteed non-null here (loading gate above returns early when
  // the session exists without a user), but narrow explicitly for the type checker.
  if (!userId || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-center">
          <div className="flex justify-center">
            <Building2 size={48} className="text-[#859F4A]" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-black">
            Adapt Hub
          </h2>
        </div>
        <SupabaseAuthForm mode="sign_in" tenantId={undefined} redirectTo={undefined} />
        <div className="w-full max-w-sm mt-6">
          <button
            onClick={() => {
              localStorage.setItem("adapthub_login_method", "apaleo");
              window.location.href = "/api/auth/connect?mode=login";
            }}
            className="w-full bg-[#859F4A] hover:bg-[#6c823b] text-white py-3.5 px-4 rounded-xl font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer"
          >
            <Activity size={18} />
            <span>Mit Apaleo einloggen</span>
          </button>
        </div>
      </div>
    );
  }

  const currentTenantId = user?.tenantId;
  const showOnboarding = currentTenantId && tenantConfig && !tenantConfig.onboardingCompleted && userRole === "admin";
  const showOnboardingLock = currentTenantId && tenantConfig && !tenantConfig.onboardingCompleted && userRole !== "admin";

  if (showOnboarding) {
    return (
      <OnboardingWizard
        tenantId={currentTenantId}
        onComplete={(config) => setTenantConfig(config as unknown as TenantConfig)}
      />
    );
  }

  if (showOnboardingLock) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden font-sans text-white">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[100px] animate-pulse duration-[10s]" />
        <div className="relative max-w-lg w-full bg-white/5 border border-white/10 rounded-[3.5rem] p-10 md:p-12 shadow-2xl text-center animate-in zoom-in">
          <div className="flex justify-center mb-8">
            <div className="w-24 h-24 bg-accent/10 rounded-[2.5rem] shadow-inner flex items-center justify-center text-accent border border-accent/20 animate-pulse">
              <Lock size={54} />
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-white display-font tracking-tight mb-4 uppercase">
            Einrichtung Ausstehend
          </h2>
          <p className="text-[11px] font-black text-accent uppercase tracking-[0.25em] mb-6">
            Setup-Phase läuft
          </p>
          <p className="text-sm text-slate-300 leading-relaxed mb-10">
            Bitte warten Sie, bis Ihr Administrator das erste Setup für Ihren Account <strong>{currentTenantId}</strong> abgeschlossen hat.
          </p>
        </div>
      </div>
    );
  }

  // overflow-hidden removed (2026-10-13): on small phone viewports the button
  // sat below the fold and was unreachable — Kamila could not sign out from
  // this screen. p-6 md:p-12 keeps the desktop look while shrinking mobile padding.
  if (user && !user.tenantId && userRole !== "guest") {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-y-auto font-sans text-white">
        <div className="relative max-w-lg w-full bg-white/5 border border-white/10 rounded-[3.5rem] p-6 md:p-12 shadow-2xl text-center my-8">
          <div className="flex justify-center mb-8">
            <Loader2 size={54} className="animate-spin text-accent" />
          </div>
          <h2 className="text-2xl font-black text-white display-font tracking-tight mb-4 uppercase">
            Tenant wird aufgelöst...
          </h2>
          <p className="text-sm text-slate-300 leading-relaxed mb-10">
            Ihr Account wird gerade konfiguriert. Bitte haben Sie einen Moment Geduld. Falls dieser Bildschirm bleibt, wenden Sie sich bitte an Ihren Administrator oder loggen Sie sich erneut ein.
          </p>
          <button onClick={() => { signOut(); localStorage.clear(); }} className="w-full bg-white/5 hover:bg-white/10 text-white border border-white/10 py-4.5 rounded-2xl font-black text-[11px] uppercase tracking-widest transition-all cursor-pointer">
            {t('common.logout')}
          </button>
        </div>
      </div>
    );
  }

  if (user && (subscriptionStatus === "suspended" || subscriptionStatus === "unpaid")) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden font-sans text-white">
        {/* Glowing aura backgrounds */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[100px] animate-pulse duration-[10s]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-rose-500/10 rounded-full blur-[100px] animate-pulse duration-[8s]" />

        <div className="relative max-w-lg w-full bg-white/5 border border-white/10 rounded-[3.5rem] p-10 md:p-12 shadow-2xl text-center animate-in zoom-in">
          <div className="flex justify-center mb-8">
            <div className="w-24 h-24 bg-rose-500/10 rounded-[2.5rem] shadow-inner flex items-center justify-center text-rose-500 border border-rose-500/20 animate-pulse">
              <AlertTriangle size={54} />
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl font-black text-white display-font tracking-tight mb-4 uppercase">
            Abonnement Pausiert
          </h2>

          <p className="text-[11px] font-black text-rose-400 uppercase tracking-[0.25em] mb-6">
            Zahlung Ausstehend oder Account Deaktiviert
          </p>

          <p className="text-sm text-slate-300 leading-relaxed mb-10">
            Der Zugriff auf <strong>AdaptHub</strong> für Ihren Account <strong>{user.tenantId}</strong> ist vorübergehend eingeschränkt. Bitte aktualisieren Sie Ihre Zahlungsmethode oder kontaktieren Sie Ihren Administrator, um den Dienst fortzusetzen.
          </p>

          <div className="space-y-4">
            <a
              href="https://identity.apaleo.com/account/apps"
              target="_blank"
              rel="noreferrer"
              className="block w-full bg-white text-slate-950 hover:bg-slate-200 py-4.5 rounded-2xl font-black text-[11px] uppercase tracking-widest transition-all shadow-xl font-bold cursor-pointer text-center"
            >
              Zum Apaleo App Store
            </a>

            <button
              onClick={() => {
                logGlobalAction(user, "Logout", "Benutzer hat sich aufgrund einer Zahlungssperre abgemeldet.");
                signOut();
                localStorage.removeItem("adapthub_login_method");
                localStorage.removeItem("adapthub_is_guest");
                localStorage.removeItem("adapthub_guest_tenant");
              }}
              className="w-full bg-white/5 hover:bg-white/10 text-white border border-white/10 py-4.5 rounded-2xl font-black text-[11px] uppercase tracking-widest transition-all cursor-pointer"
            >
              {t('common.logout')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthedApp
      user={user}
      userRole={userRole}
      isApaleoAuthed={isApaleoAuthed}
      setIsApaleoAuthed={setIsApaleoAuthed}
      isOffline={isOffline}
      tenantConfig={tenantConfig}
      setTenantConfig={setTenantConfig}
      globalAdmins={globalAdmins}
      signOut={signOut}
      getToken={getToken}
    />
  );
}

